<script setup>
/**
 * One proposal of answers Claude drafted (services spec §6 P-6.2, §8 P-8.4). Not used by the
 * assistant until she keeps them. Keep and Keep all are priced and asked first (`keep_answers`:
 * one `teach` per Keep of up to fifty); Remove is free. A Keep is its own batch, with its own Undo.
 *
 * THE LIST IS KEPT HERE, NOT RE-READ. The sidecar narrows the proposal exactly as `narrowed`
 * below does (the pairs not picked, in order, numbered again from 0), so the screen follows it
 * without a reload, and the "Kept" line with its receipt and Undo stays on screen after the last
 * pair is kept, when the proposal itself is gone.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from './AiConfirmInline.vue';
import ClaudeChip from './ClaudeChip.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useWasloI18n } from '../i18n.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  /** A Claude `answers` proposal (`wasloApi.getSetupSuggestions(accountId, 'claude')`). */
  suggestion: { type: Object, required: true },
});

const emit = defineEmits(['kept', 'removed']);

const { t } = useWasloI18n();
const price = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'keep_answers',
});

const items = ref([...(props.suggestion.claude?.items ?? [])]);
watch(
  () => props.suggestion.id,
  () => {
    items.value = [...(props.suggestion.claude?.items ?? [])];
  }
);

/** What the sidecar leaves open after these pairs go: the rest, in order, numbered from 0. */
const narrowed = gone =>
  items.value
    .filter(item => !gone.includes(item.index))
    .map((item, index) => ({ ...item, index }));

const busy = ref(false);
const failure = ref('');
const kept = ref(null);
const undone = ref(false);
const receipt = computed(() => price.receiptText.value);

const keep = async pick => {
  if (busy.value || props.accountId === null || pick.length === 0) return;
  busy.value = true;
  failure.value = '';
  try {
    const outcome = await price.confirmAndRun({
      count: pick.length,
      run: credits =>
        wasloApi.keepAnswerDrafts(
          props.accountId,
          { suggestionId: props.suggestion.id, pick },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    kept.value = outcome.result;
    undone.value = false;
    items.value = narrowed(pick);
    emit('kept', outcome.result);
  } catch (error) {
    failure.value = error?.message || t('WASLO.CLAUDE.ANSWERS.FAILED');
  } finally {
    busy.value = false;
  }
};

const remove = async index => {
  if (busy.value || props.accountId === null) return;
  busy.value = true;
  failure.value = '';
  try {
    await wasloApi.removeAnswerDrafts(props.accountId, props.suggestion.id, [
      index,
    ]);
    items.value = narrowed([index]);
    emit('removed');
  } catch (error) {
    failure.value = error?.message || t('WASLO.CLAUDE.REVIEW.FAILED');
  } finally {
    busy.value = false;
  }
};

const undo = async () => {
  if (busy.value || kept.value === null || props.accountId === null) return;
  busy.value = true;
  try {
    await wasloApi.undoBatch(props.accountId, kept.value.batchId);
    undone.value = true;
  } catch (error) {
    failure.value = error?.message || t('WASLO.CLAUDE.REVIEW.FAILED');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-3"
    data-waslo-claude-answers
    :data-suggestion="suggestion.id"
  >
    <div v-if="kept" class="flex flex-col gap-1" data-waslo-claude-answers-kept>
      <p class="mb-0 text-sm text-n-slate-12">
        {{
          undone
            ? t('WASLO.SETUP_AI.CHANGES.UNDONE')
            : t('WASLO.CLAUDE.ANSWERS.KEPT')
        }}
      </p>
      <p
        v-if="receipt"
        class="mb-0 text-xs text-n-slate-11"
        data-waslo-ai-receipt
      >
        {{ receipt }}
      </p>
      <p
        v-if="kept.conflicts > 0 && !undone"
        class="mb-0 text-xs text-n-amber-11"
        data-waslo-claude-answers-conflicts
      >
        {{ t('WASLO.CLAUDE.ANSWERS.CONFLICTS') }}
      </p>
      <Button
        v-if="!undone"
        :label="t('WASLO.SETUP_AI.UNDO')"
        size="sm"
        variant="faded"
        color="slate"
        :disabled="busy"
        data-waslo-claude-answers-undo
        @click="undo"
      />
    </div>

    <div
      v-for="item in items"
      :key="item.index"
      class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
      :data-waslo-claude-answer="item.index"
    >
      <ClaudeChip />
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        <bdi>{{ item.question }}</bdi>
      </p>
      <p class="mb-0 text-sm whitespace-pre-wrap text-n-slate-11">
        <bdi>{{ item.answer }}</bdi>
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          :label="price.label(t('WASLO.CLAUDE.ANSWERS.KEEP'), 1)"
          size="sm"
          :disabled="busy"
          data-waslo-claude-answer-keep
          @click="keep([item.index])"
        />
        <Button
          :label="t('WASLO.CLAUDE.ANSWERS.REMOVE')"
          size="sm"
          variant="faded"
          color="slate"
          :disabled="busy"
          data-waslo-claude-answer-remove
          @click="remove(item.index)"
        />
      </div>
    </div>
    <Button
      v-if="items.length > 1"
      :label="price.label(t('WASLO.CLAUDE.ANSWERS.KEEP_ALL'), items.length)"
      :disabled="busy"
      data-waslo-claude-answers-keep-all
      @click="keep(items.map(item => item.index))"
    />
    <AiConfirmInline :controller="price" />

    <p v-if="failure" class="mb-0 text-sm text-n-ruby-11">{{ failure }}</p>
  </section>
</template>
